import { useContext, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { SettingsContext, useEntitlement } from "../../contexts";

function parseSkipCompCheck(listing) {
  const defaults = { ebay: false, chrono: false, wf: false, dao: false };
  const raw = listing?.skip_comp_check;
  if (!raw) return defaults;
  if (typeof raw === "object") return { ...defaults, ...raw };
  if (typeof raw === "string") {
    try {
      return { ...defaults, ...JSON.parse(raw) };
    } catch (_) {
      console.warn("Failed to parse skip_comp_check JSON:", _);
    }
  }
  return defaults;
}

export function useListingDetailsRow({
  listing,
  isNetwork = false,
  importListing,
  assignListing,
  isSelectListing = false,
}) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const { isShopify, iframeQueryParams, settings } = useContext(SettingsContext);
  const hasCompCheck = useEntitlement("competitive_pricing_check");
  const product = listing?.product;

  const [reserved, setReserved] = useState(listing.is_reserved);
  const [chrono, setChrono] = useState(
    listing.is_chrono && listing.status === "active"
  );
  const [ebay, setEbay] = useState(
    listing.is_ebay && listing.status === "active"
  );
  const [internalProduct, setInternalProduct] = useState(listing.internal_product);
  // Owned By values this two-state switch cannot express. Consignment was
  // already one of them; "to_be_procured" is the second. Both are locked rather
  // than silently rewritten to "owned"/"no" on the next click.
  const internalProductLocked =
    internalProduct === "exclusive" || internalProduct === "to_be_procured";
  const [watchFinder, setWatchFinder] = useState(listing.is_wf);
  const [active, setActive] = useState(listing.status === "active");
  const [deleted, setDeleted] = useState(false);
  const [showPriceWarningModal, setShowPriceWarningModal] = useState(false);
  const [pendingToggleType, setPendingToggleType] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [skipCompCheck, setSkipCompCheck] = useState(() =>
    parseSkipCompCheck(listing)
  );

  // Intelligence add-on (Feature 10): the "needs pricing review" acknowledge
  // checkboxes belong to the competitive pricing check, so they disappear
  // with it. The comparison DATA stays in the payload (the external checker
  // keeps writing it) — we only stop surfacing it.
  const shouldShowCheckbox = (platform) =>
    hasCompCheck && listing[`${platform}_is_comp`] === 0;

  const handleRowClick = (e) => {
    if (
      e.target.closest(
        "button, input, select, textarea, a, .switch, .dropdown, .comp-checkbox, .lb-lcard"
      )
    ) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handleCompCheckboxClick = async (platform, e) => {
    e.stopPropagation();
    const newSkipCompCheck = {
      ...skipCompCheck,
      [platform]: !skipCompCheck[platform],
    };
    setSkipCompCheck(newSkipCompCheck);
    try {
      await axios.put(`/api/listings/${listing.id}/skip-comp-check`, {
        skip_comp_check: newSkipCompCheck,
      });
    } catch (error) {
      setSkipCompCheck(skipCompCheck);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update competitive pricing settings";
      alert.show(message);
    }
  };

  const onSyncChronoChange = async (val) => {
    if (val && (listing.chrono_price === 0 || listing.chrono_price === "0")) {
      setPendingToggleType("chrono");
      setShowPriceWarningModal(true);
      return;
    }
    setChrono(val);
    try {
      await axios.put(`/api/listings/${listing.id}/chrono`, { is_chrono: val });
    } catch (error) {
      // Plan-gated 403s toast via the global EntitlementErrorListener —
      // here we only snap the optimistic toggle back.
      setChrono(!val);
    }
  };

  const onSyncEbayChange = async (val) => {
    if (val && (listing.ebay_price === 100 || listing.ebay_price === "100")) {
      setPendingToggleType("ebay");
      setShowPriceWarningModal(true);
      return;
    }
    setEbay(val);
    try {
      await axios.put(`/api/listings/${listing.id}/ebay`, { is_ebay: val });
    } catch (error) {
      setEbay(!val);
      // Plan-gated 403s toast via the global EntitlementErrorListener.
      if (error?.response?.data?.code !== "feature_locked") {
        showApiError(error, "update the eBay listing");
      }
    }
  };

  const onReserveChange = (val, forId) => {
    setReserved(val);
    axios.put(`/api/listings/${listing.id}/reserved`, {
      reserved: val,
      reserved_for: val ? forId : null,
    });
  };

  // The switch only ever expresses "owned" vs "no". Owned By has two more
  // values it cannot say — "exclusive" and "to_be_procured" — and writing over
  // either of them on a single click is silent data loss, so the switch is
  // locked (and reads OFF) for both. Reclassifying is a deliberate act in the
  // listing form's Owned By dropdown.
  const onInternalProductChange = (ownedByDao) => {
    if (internalProductLocked) return;
    const owner = ownedByDao ? "owned" : "no";
    setInternalProduct(owner);
    axios.put(`/api/listings/${listing.id}/internal_product`, {
      internal_product: owner,
    });
  };

  const onActiveChange = (val) => {
    setActive(val);
    listing.status = val ? "active" : "deactivated";
    axios.put(`/api/listings/${listing.id}/active`, { active: val });
  };

  const onDeleteClick = async () => {
    if (confirm("Are you sure?")) {
      await axios.delete(`/api/listings/${listing.id}`);
      setDeleted(true);
    }
  };

  const onEditClick = () => {
    window.open(
      `/product/${listing.id}/edit/product?${iframeQueryParams}`,
      "_blank"
    );
  };

  const onSyncWatchFinderChange = async (val) => {
    setWatchFinder(val);
    try {
      await axios.put(`/api/listings/${listing.id}/watchfinder`, {
        wf_url: null,
        is_wf: 0,
      });
    } catch (error) {
      setWatchFinder(!val);
      // Plan-gated 403s already toast via the global EntitlementErrorListener
      // — only surface the generic alert for other failures.
      if (error?.response?.data?.code !== "feature_locked") {
        showApiError(error, "update the Watchfinder listing");
      }
    }
  };

  const handleModalEdit = () => {
    onEditClick();
    setShowPriceWarningModal(false);
    setPendingToggleType(null);
  };

  const handleModalConfirm = async () => {
    if (pendingToggleType === "chrono") {
      setChrono(true);
      try {
        await axios.put(`/api/listings/${listing.id}/chrono`, { is_chrono: true });
      } catch (error) {
        setChrono(false); // plan-gated 403 toasts globally; revert the toggle
      }
    } else if (pendingToggleType === "ebay") {
      setEbay(true);
      try {
        await axios.put(`/api/listings/${listing.id}/ebay`, { is_ebay: true });
      } catch (error) {
        setEbay(false);
        showApiError(error, "update the eBay listing");
        return;
      }
    }
    setShowPriceWarningModal(false);
    setPendingToggleType(null);
  };

  const closeModal = () => {
    setShowPriceWarningModal(false);
    setPendingToggleType(null);
  };

  const activeSwitchProps = {
    onChange: isNetwork ? () => {} : onActiveChange,
    checked: active,
  };

  const inStockSwitchProps = {
    onChange: () => {},
    checked: isNetwork ? !listing.sold : listing.status !== "deleted",
  };

  const editButtonProps = {
    label: "Edit",
    onClick: onEditClick,
    options: [
      { label: `Listing N*: ${listing.reference_number}` },
      { label: "Delete", onClick: onDeleteClick },
    ],
  };

  const importButtonProps = {
    label: "Import",
    onClick: isNetwork ? () => importListing(listing.id) : () => {},
    submitting: "Importing...",
    success: "Imported",
  };

  const daoLabel = settings?.acronym || settings?.name || "DAO";
  const isActiveListing = listing.status === "active";
  // ON only for the value it actually means. The old "not no and not exclusive"
  // read ON for anything unrecognised — with a fourth enum value that meant a
  // "to_be_procured" listing displayed as store-owned and saved itself back as
  // "owned" the moment anyone touched it.
  const inStockOn = internalProduct === "owned";

  return {
    product,
    settings,
    isShopify,
    reserved,
    chrono,
    ebay,
    internalProduct,
    watchFinder,
    active,
    deleted,
    showPriceWarningModal,
    pendingToggleType,
    isExpanded,
    skipCompCheck,
    shouldShowCheckbox,
    handleRowClick,
    handleCompCheckboxClick,
    onSyncChronoChange,
    onSyncEbayChange,
    onReserveChange,
    onInternalProductChange,
    onActiveChange,
    onDeleteClick,
    onEditClick,
    onSyncWatchFinderChange,
    handleModalEdit,
    handleModalConfirm,
    closeModal,
    activeSwitchProps,
    inStockSwitchProps,
    editButtonProps,
    importButtonProps,
    daoLabel,
    isActiveListing,
    inStockOn,
    internalProductLocked,
    isNetwork,
    isSelectListing,
    assignListing,
  };
}
